import { createContext, useContext, useRef, useState, ReactNode, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLeadSuccess } from "@/hooks/useLeadSuccess";
import { submitLead } from "@/lib/leadSubmit";
import { isAcceptableEmail, isDisposableEmail, isSpamSubmission, isValidPhone, type Division } from "@/lib/leadGuard";
import { countryNameEs, isCountryCode } from "@/lib/countries";
import { CountrySelect, HoneypotField } from "./LeadFields";

type OpenArgs = { url: string; division: Division; label?: string };

type Ctx = { openDossier: (args: OpenArgs) => void };

const DossierCtx = createContext<Ctx>({ openDossier: () => {} });

export const useDossier = () => useContext(DossierCtx);

const inputClass =
  "w-full rounded-none border border-white/25 bg-transparent px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-white focus:outline-none";

export const DossierGateProvider = ({ children }: { children: ReactNode }) => {
  const onSuccess = useLeadSuccess();
  const [target, setTarget] = useState<OpenArgs | null>(null);
  const [sending, setSending] = useState(false);
  const [nombre, setNombre] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [pais, setPais] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const startedAt = useRef(0);
  const { t, i18n } = useTranslation();

  const close = () => {
    setTarget(null);
    setSending(false);
  };

  const openDossier = (args: OpenArgs) => {
    startedAt.current = Date.now();
    setHoneypot("");
    setFormError(null);
    setTarget(args);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!target || sending) return;
    if (!empresa.trim() || !isCountryCode(pais)) {
      setFormError(t("sFormsContact.errors.checkData"));
      return;
    }
    if (!isAcceptableEmail(email)) {
      setFormError(isDisposableEmail(email) ? t("sLeadFields.disposableEmail") : t("sLeadFields.invalidEmail"));
      return;
    }
    if (!isValidPhone(telefono)) {
      setFormError(t("sLeadFields.invalidPhone"));
      return;
    }
    setFormError(null);
    setSending(true);

    const division = target.division;
    const form = division === "industrial" ? "dosier-industrial" : "dosier-retail";
    const spam = isSpamSubmission({ honeypot, startedAt: startedAt.current });

    // Abrir el dosier al instante (dentro del clic, así el navegador no lo bloquea)
    window.open(target.url, "_blank", "noopener,noreferrer");

    // Guardar y avisar en segundo plano: el visitante no espera
    void submitLead({
      core: {
        nombre: nombre.trim(),
        empresa: empresa.trim(),
        email: email.trim(),
        telefono: telefono.trim(),
        pais: countryNameEs(pais),
        mensaje: mensaje.trim(),
      },
      extras: { division, form_name: form, request_type: "dosier", country_code: pais },
      lang: i18n.language,
      honeypot,
      startedAt: startedAt.current,
    }).catch(() => {
      /* noop */
    });

    const dossierUrl = target.url;
    close();
    onSuccess(form, { dossierUrl }, { division, request_type: "dosier", spam });
  };

  return (
    <DossierCtx.Provider value={{ openDossier }}>
      {children}
      {target && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 px-4 py-8 overflow-y-auto">
          <div className="w-full max-w-lg border border-white/15 bg-black p-6 md:p-8">
            <p className="font-dot text-[10px] uppercase tracking-widest text-white/50">
              Acceso al dosier
            </p>
            <h2 className="mt-3 font-serif text-2xl md:text-3xl leading-tight text-white">
              {target.label ?? "Déjanos tus datos y te enviamos el dosier"}
            </h2>

            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <HoneypotField value={honeypot} onChange={setHoneypot} />
              <input
                required
                maxLength={100}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre y apellidos"
                aria-label="Nombre y apellidos"
                className={inputClass}
              />
              <input
                required
                maxLength={120}
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                placeholder="Empresa"
                aria-label="Empresa"
                className={inputClass}
              />
              <CountrySelect value={pais} onChange={setPais} className={`${inputClass} appearance-none`} />
              <input
                required
                type="email"
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email corporativo"
                aria-label="Email corporativo"
                className={inputClass}
              />
              <input
                required
                type="tel"
                maxLength={30}
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="Teléfono"
                aria-label="Teléfono"
                className={inputClass}
              />
              <textarea
                required
                maxLength={1000}
                rows={3}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Cuéntanos brevemente tu proyecto"
                aria-label="Mensaje"
                className={`${inputClass} resize-none`}
              />

              {formError && <p className="text-sm text-red-400">{formError}</p>}
              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={close}
                  className="font-dot text-xs uppercase tracking-widest text-white/50 underline underline-offset-4"
                >
                  Cerrar
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="rounded-none bg-white px-6 py-3 text-base text-black transition-opacity disabled:opacity-50"
                >
                  {sending ? "Enviando…" : "Descargar dosier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DossierCtx.Provider>
  );
};
